import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import PostEditor from '../components/PostEditor';
import '../styles/admin-common.css';
import { formatDate } from '../utils/format';
import { TableScroll } from '../components/ui';

const CATEGORY_LABELS: Record<string, string> = {
  notice: '공지사항',
  study: '스터디',
  code: '코드공유',
  general: '일반',
};

const BoardManagementPage: React.FC = () => {
  const { user } = useAuth();
  const [boardPosts, setBoardPosts] = useState<any[]>([]);
  const [editingPost, setEditingPost] = useState<any>(null);
  const [showPostEditor, setShowPostEditor] = useState(false);
  const [loading, setLoading] = useState(false);

  const apiCall = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('authToken');
    return fetch(`${import.meta.env.VITE_API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });
  };

  const loadBoardPosts = async () => {
    setLoading(true);
    try {
      const response = await apiCall('/admin/board/posts?limit=50');
      if (response.ok) {
        const result = await response.json();
        setBoardPosts(result.data?.posts ?? []);
      }
    } catch (error) {
      console.error('Failed to load board posts:', error);
    }
    setLoading(false);
  };

  const savePost = async (postData: any) => {
    try {
      const url = editingPost ? `/admin/board/posts/${editingPost.post_id}` : '/admin/board/posts';
      const method = editingPost ? 'PUT' : 'POST';

      const response = await apiCall(url, {
        method,
        body: JSON.stringify(postData),
      });

      if (response.ok) {
        setEditingPost(null);
        setShowPostEditor(false);
        loadBoardPosts();
      }
    } catch (error) {
      console.error('Failed to save post:', error);
    }
  };

  const deletePost = async (postId: string) => {
    if (!confirm('정말 이 게시글을 삭제하시겠습니까?')) return;

    try {
      const response = await apiCall(`/admin/board/posts/${postId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        loadBoardPosts();
      }
    } catch (error) {
      console.error('Failed to delete post:', error);
    }
  };

  useEffect(() => {
    loadBoardPosts();
  }, []);

  if (!user?.is_admin) {
    return (
      <div className="admin-page">
        <div className="access-denied">
          <h2>접근 권한이 없습니다</h2>
          <p>관리자 권한이 필요합니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>게시판 관리</h1>
      </div>

      {loading && <div className="loading">로딩 중...</div>}

      <div className="admin-section">
        <div className="admin-header">
          <h2>게시판 관리</h2>
          <button
            className="admin-btn success"
            onClick={() => {
              setEditingPost(null);
              setShowPostEditor(true);
            }}
          >
            게시글 작성
          </button>
        </div>

        <TableScroll>
          <table className="admin-table">
            <thead>
              <tr>
                <th>제목</th>
                <th>카테고리</th>
                <th>작성자</th>
                <th>작성일</th>
                <th>조회수</th>
                <th>작업</th>
              </tr>
            </thead>
            <tbody>
              {boardPosts.map(post => (
                <tr key={post.post_id}>
                  <td>
                    <div className="admin-flex admin-items-center admin-gap-2">
                      {post.is_pinned && <span className="admin-badge info">고정</span>}
                      <span>{post.title}</span>
                    </div>
                  </td>
                  <td>
                    <span className="admin-badge secondary">{CATEGORY_LABELS[post.category] ?? post.category}</span>
                  </td>
                  <td>
                    <span className="admin-text-sm">{post.author_name}</span>
                  </td>
                  <td>
                    <span className="admin-text-sm">{formatDate(post.created_at)}</span>
                  </td>
                  <td>
                    <span className="admin-badge secondary">{post.view_count}</span>
                  </td>
                  <td>
                    <div className="admin-flex admin-gap-2">
                      <button className="admin-btn small outline" onClick={() => {
                        setEditingPost(post);
                        setShowPostEditor(true);
                      }}>수정</button>
                      <button className="admin-btn small danger" onClick={() => deletePost(post.post_id)}>삭제</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      </div>

      {showPostEditor && (
        <PostEditor
          post={editingPost}
          onSave={savePost}
          onCancel={() => {
            setShowPostEditor(false);
            setEditingPost(null);
          }}
        />
      )}
    </div>
  );
};

export default BoardManagementPage;